import api from './axiosConfig';
import { handleApiError } from './errorHandler';

const receiveBatch = async (items) => {
    try {
        const { data } = await api.post('/api/inventory-items/receive-batch/', items);
        return data;
    } catch (error) {
        handleApiError(error, 'Receive batch inventory items error:');
        throw error;
    }
};

export const inventoryItemAPI = {
    receiveBatch,
    getRecentReceives: async (days = 7, page = 1, pageSize = 10, searchTerm = '') => {
        try {
            const { data } = await api.get('/api/inventory-items/recent-receives/', {
                params: {
                    days,
                    page,
                    pageSize,
                    searchTerm
                }
            });
            return {
                ...data,
                results: {
                    ...data.results,
                    total_quantity: data.results.total_quantity || 0,
                    total_cases: data.results.total_cases || 0
                }
            };
        } catch (error) {
            handleApiError(error, 'Get recent receives error:');
            throw error;
        }
    },
    create: async (inventoryItemData) => {
        try {
            const cleanedInventoryItem = {
                ...inventoryItemData,
                stock_item_id: inventoryItemData.stockItemData._id,
                bb_date: inventoryItemData.bb_date || null,
                stockItemData: undefined
            }
            const { data } = await api.post('/api/inventory-items/create/', cleanedInventoryItem);
            return data;
        } catch (error) {
            handleApiError(error, 'Create inventory item error:');
        }
    },
    getAll: async (
        page,
        pageSize,
        searchTerm, 
        vendorId,
        manufacturerId,
        categoryIds, 
        startDate, 
        endDate,
        receivedStartDate,
        receivedEndDate,
        warehouseId, 
        locationId, 
        inTransit,
        sortColumn,
        sortDirection
    ) => {
        try {
            const { data } = await api.get('/api/inventory-items', {
                params: { 
                    page, 
                    pageSize,
                    searchTerm, 
                    vendorId,
                    manufacturerId,
                    categoryIds, 
                    startDate, 
                    endDate,
                    receivedStartDate,
                    receivedEndDate,
                    warehouseId,
                    locationId,
                    inTransit,
                    sortColumn,
                    sortDirection
                }
            });
            return {
                ...data,
                results: {
                    ...data.results,
                    total_quantity: data.results.total_quantity || 0,
                    total_cases: data.results.total_cases || 0,
                    total_weight_lbs: data.results.total_weight_lbs || 0
                }
            };
        } catch (error) {
            handleApiError(error, 'Get all inventory items error:');
        }
    },
    getById: async (id) => {
        try {
            const { data } = await api.get(`/api/inventory-items/${id}`);
            return data;
        } catch (error) {
            handleApiError(error, 'Get inventory item by id error:');
        }
    },
    getByIds: async (ids) => {
        try {
            if (!ids || (Array.isArray(ids) && ids.length === 0)) {
                return {
                    results: [],
                    total_quantity: 0,
                    total_requested: 0,
                    total_found: 0
                };
            }
            
            // Convert array to comma-separated string if needed
            const idsString = Array.isArray(ids) ? ids.join(',') : ids;
            
            // Make sure we don't have any spaces in the IDs string
            const cleanIdsString = idsString.replace(/\s+/g, '');
            const { data } = await api.get(`/api/inventory-items/ids/${cleanIdsString}`);
            
            // Case 1: The backend returns an object with 'results' property containing the array of items
            if (data && data.results && Array.isArray(data.results)) {
                return data;
            } 
            
            // Case 2: The backend returns a direct array
            if (Array.isArray(data)) {
                return {
                    results: data,
                    total_quantity: data.reduce((sum, item) => sum + (item.quantity || 0), 0),
                    total_requested: ids.length,
                    total_found: data.length
                };
            }
            
            // Case 3: The backend returns a single object (not in an array)
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                return {
                    results: [data],
                    total_quantity: data.quantity || 0,
                    total_requested: 1,
                    total_found: 1
                };
            }
            
            // Default case: Return empty response if data is null, undefined, or not in expected format
            return {
                results: [],
                total_quantity: 0,
                total_requested: Array.isArray(ids) ? ids.length : 1,
                total_found: 0
            };
        } catch (error) {
            handleApiError(error, 'Get inventory items by ids error:');
            return {
                results: [],
                total_quantity: 0,
                total_requested: Array.isArray(ids) ? ids.length : 1,
                total_found: 0
            };
        }
    },
    update: async (id, inventoryItemData) => {
        try {
            const { data } = await api.put(`/api/inventory-items/update/${id}/`, inventoryItemData);
            return data;
        } catch (error) {
            handleApiError(error, 'Update inventory item error:');
        }
    },
    delete: async (id, comments) => {
        try {
            const { data } = await api.delete(`/api/inventory-items/delete/${id}/`, {
                data: comments
            });
            return data;
        } catch (error) {
            handleApiError(error, 'Delete inventory item error:');
        }
    },
    move: async (inventoryItemId, quantity, new_location_id, comments) => {
        try {
            // Note: When warehouse transit is disabled, this endpoint can also move items directly between warehouses.
            // The backend will validate if the move is allowed based on the system settings.
            const { data } = await api.post(`/api/inventory-items/move/${inventoryItemId}/`, { 
                quantity, 
                new_location_id, 
                comments 
            });
            return data;
        } catch (error) {
            // The error message will indicate if the move was rejected due to warehouse transit settings
            handleApiError(error, 'Move inventory item error:');
            throw error; // Re-throw to let the component handle specific error messages
        }
    },
    moveBulk: async (items, new_location_id, comments) => {
        try {
            // Validate input parameters
            if (!items || !Array.isArray(items) || items.length === 0) {
                throw new Error('No items to move');
            }
            
            if (!new_location_id) {
                throw new Error('Destination location is required');
            }
            
            // Note: When warehouse transit is disabled, this endpoint can also move items directly between warehouses.
            // The backend will validate:
            // 1. If cross-warehouse moves are allowed based on system settings
            // 2. That all items are from the same warehouse
            // 3. That quantities are valid
            const { data } = await api.post(`/api/inventory-items/move-bulk/`, { 
                items,
                new_location_id, 
                comments: comments || ''
            });
            return data;
        } catch (error) {
            // The error message will indicate if the move was rejected due to:
            // - Warehouse transit settings
            // - Items from different warehouses
            // - Invalid quantities
            handleApiError(error, 'Move bulk inventory items error:');
            throw error; // Re-throw to let the component handle specific error messages
        }  
    },
    issueBulk: async (items, parentDestination, subcategoryDestination, comments) => {
        try {
            // Validate input parameters
            // Items should be: [{id, quantity, sell_price}, ...]
            if (!items || !Array.isArray(items) || items.length === 0) {
                throw new Error('No items to issue');
            }
            
            if (!parentDestination) {
                throw new Error('Destination is required');
            }
            
            const { data } = await api.post(`/api/inventory-items/issue-bulk/`, { 
                items,
                destination: parentDestination,
                destination_subcategory: subcategoryDestination,
                comments: comments || ''
            });
            return data;
        } catch (error) {
            handleApiError(error, 'Issue bulk inventory items error:');
            throw error; // Re-throw to let the component handle it
        }  
    },
    disposeBulk: async (items, reason, comments) => {
        try {
            // Validate input parameters
            if (!items || !Array.isArray(items) || items.length === 0) {
                throw new Error('No items to dispose');
            }
            
            const { data } = await api.post(`/api/inventory-items/dispose-bulk/`, { 
                items,
                reason: reason || 'OTHER', 
                comments: comments || ''
            });
            return data;
        } catch (error) {
            handleApiError(error, 'Dispose bulk inventory items error:');
            throw error; // Re-throw to let the component handle it
        }  
    },
    dispose: async (inventoryItemId, quantity, reason, comments) => {
        try {
            const { data } = await api.post(`/api/inventory-items/dispose/${inventoryItemId}/`, { quantity, reason, comments });
            return data;
        } catch (error) {
            handleApiError(error, 'Dispose inventory item error:');
        }
    },
    return: async (inventoryItemId, quantity, reason, comments) => {
        try {
            const { data } = await api.post(`/api/inventory-items/return/${inventoryItemId}/`, { quantity, reason, comments });
            return data;
        } catch (error) {
            handleApiError(error, 'Return inventory item error:');
        }
    },
    transfer: async (inventoryItemId, quantity, new_warehouse_id, comments) => {
        try {
            const { data } = await api.post(`/api/inventory-items/transfer/${inventoryItemId}/`, { quantity, new_warehouse_id, comments });
            return data;
        } catch (error) {
            handleApiError(error, 'Transfer inventory item error:');
        }
    },
    issue: async (inventoryItemId, quantity, comments, parentDestination, subcategoryDestination, sellPrice) => {
        try {
            const payload = { 
                quantity, 
                comments,
                destination: parentDestination,
                destination_subcategory: subcategoryDestination,
                sell_price: sellPrice
            };
            const { data } = await api.post(`/api/inventory-items/issue/${inventoryItemId}/`, payload);
            return data;
        } catch (error) {
            handleApiError(error, 'Issue inventory item error:');
        }
    },
    generateInventoryReport: async (
        searchTerm, 
        vendorId,
        manufacturerId,
        categoryIds, 
        startDate, 
        endDate, 
        warehouseId, 
        locationId, 
        inTransit,
        fileType
    ) => {
        try {
            const response = await api.get('/api/reports/inventory-items/', {
                params: { 
                    searchTerm, 
                    vendorId,
                    manufacturerId,
                    categoryIds, 
                    startDate, 
                    endDate,
                    warehouseId,
                    locationId,
                    inTransit,
                    fileType
                },
                responseType: 'blob'  // Important for handling file downloads
            });
            
            // Create a URL for the blob
            const url = window.URL.createObjectURL(new Blob([response.data]));
            
            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            
            // Create timestamp in format YYYYMMDD_HHMMSS
            const timestamp = new Date().toISOString()
                .replace(/[:\-T]/g, '')
                .slice(0, 14);
            const fileName = `inventory_report_${timestamp}.${fileType}`;
            link.setAttribute('download', fileName);
            
            // Append to body, click, and clean up
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            return true;
        } catch (error) {
            handleApiError(error, 'Generate inventory report error:');
            return false;
        }
    }
};
